import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Calendar, Clock, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API } from "@/App";

export default function BookingSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  // Use ref to track poll count to avoid stale closure issues
  const pollCountRef = useRef(0);
  const MAX_ATTEMPTS = 5;
  const POLL_INTERVAL = 2000;

  const pollPaymentStatus = useCallback(async () => {
    if (pollCountRef.current >= MAX_ATTEMPTS) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/bookings/status/${sessionId}`);
      setPaymentData(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      pollCountRef.current += 1;
      setTimeout(pollPaymentStatus, POLL_INTERVAL);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("error");
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      navigate("/");
      return;
    }
    
    // Reset poll count when component mounts or sessionId changes
    pollCountRef.current = 0;
    pollPaymentStatus();
  }, [sessionId, navigate, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-obsidian flex flex-col">
      {/* Main Content - Centered */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <motion.div 
          className="max-w-lg w-full text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          {status === "loading" && (
            <>
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-charcoal border border-slate-800 rounded-full flex items-center justify-center mx-auto mb-6 sm:mb-8">
                <Loader2 className="w-8 h-8 sm:w-10 sm:h-10 text-gold-500 animate-spin" />
              </div>
              <h1 className="font-display text-2xl sm:text-4xl mb-3 sm:mb-4">Processing Payment...</h1>
              <p className="text-slate-400 text-sm sm:text-base mb-6 sm:mb-8">Please wait while we confirm your booking.</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 sm:mb-8">
                <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10 text-green-500" />
              </div>
              <h1 className="font-display text-2xl sm:text-4xl mb-3 sm:mb-4">Booking Confirmed!</h1>
              <p className="text-slate-400 text-sm sm:text-base mb-6 sm:mb-8 px-4">
                Your session has been booked successfully. You'll receive a confirmation email shortly.
              </p>
              
              {paymentData && (
                <div className="bg-charcoal border border-slate-800 p-4 sm:p-6 rounded-sm mb-6 sm:mb-8 text-left mx-4 sm:mx-0">
                  <h3 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Booking Details</h3>
                  <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Amount Paid</span>
                      <span className="font-semibold text-gold-500">${paymentData.amount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Status</span>
                      <span className="text-green-500 font-medium">Confirmed</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col gap-3 sm:gap-4 px-4 sm:px-0">
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="bg-gold-500 hover:bg-gold-400 text-black font-semibold px-6 sm:px-8 h-12"
                  data-testid="view-bookings-btn"
                >
                  View My Bookings
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button 
                  onClick={() => navigate("/experts")}
                  variant="outline"
                  className="border-slate-700 h-12"
                >
                  Browse More Experts
                </Button>
              </div>
            </>
          )}

          {status === "expired" && (
            <>
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6 sm:mb-8">
                <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-yellow-500" />
              </div>
              <h1 className="font-display text-2xl sm:text-4xl mb-3 sm:mb-4">Session Expired</h1>
              <p className="text-slate-400 text-sm sm:text-base mb-6 sm:mb-8 px-4">
                The payment session has expired. Please try booking again.
              </p>
              <div className="px-4 sm:px-0">
                <Button 
                  onClick={() => navigate("/experts")}
                  className="bg-gold-500 hover:bg-gold-400 text-black font-semibold px-6 sm:px-8 h-12 w-full sm:w-auto"
                >
                  Browse Experts
                </Button>
              </div>
            </>
          )}

          {(status === "error" || status === "timeout") && (
            <>
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6 sm:mb-8">
                <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-red-500" />
              </div>
              <h1 className="font-display text-2xl sm:text-4xl mb-3 sm:mb-4">Something Went Wrong</h1>
              <p className="text-slate-400 text-sm sm:text-base mb-6 sm:mb-8 px-4">
                We couldn't verify your payment. Please check your email for confirmation or contact support.
              </p>
              <div className="flex flex-col gap-3 sm:gap-4 px-4 sm:px-0">
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="bg-gold-500 hover:bg-gold-400 text-black font-semibold px-6 sm:px-8 h-12"
                >
                  Check My Bookings
                </Button>
                <Button 
                  onClick={() => navigate("/")}
                  variant="outline"
                  className="border-slate-700 h-12"
                >
                  Go Home
                </Button>
              </div>
            </>
          )}

          <Link 
            to="/"
            className="inline-block mt-8 sm:mt-12 text-slate-500 hover:text-gold-500 transition-colors"
          >
            <span className="font-display text-lg sm:text-xl text-gold-gradient">ExpertBase</span>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
